import React, { useState, useRef, useEffect } from "react";
import voyageheader from "../assets/images/voyageheader.webp";
import contact from "../assets/images/contact.webp";
import voyagelogo from "../assets/images/voyagelogo.webp";
import IATAlogo from "../assets/images/IATAlogo.webp";
import IATAlogocolor from "../assets/images/IATAlogocolor.webp";
import gallery from "../assets/images/gallery.webp";
import faq from "../assets/images/faq.webp";
import { getNames } from "country-list";
import emailjs from "@emailjs/browser";

// Menu configuration
const menus = [
  {
    title: "About",
    href: "/about",
    dropdownHeader: {
      title: "Trust & Experience",
      description: "Learn about our passion for travel and journeys.",
      bg: "bg-green-50",
      text: "text-green-700",
      icon: "las la-users",
    },
    items: [
      { title: "Who We Are", href: "/about#who-we-are" },
      { title: "Why Choose Us", href: "/about#why-choose-us" },
      { title: "Where We Come From", href: "/about#where-we-come-from" },
      { title: "Where We Work", href: "/about#where-we-come-from" },
    ],
  },
  {
    title: "Services",
    href: "/services",
    dropdownHeader: {
      title: "Premium Services",
      description: "We manage everything for you",
      bg: "bg-blue-50",
      text: "text-blue-700",
      icon: "las la-concierge-bell",
    },
    items: [
      {
        title: "Global Airlines Ticketing",
        href: "/services/#global-airline-ticketing",
      },
      {
        title: "Worldwide Hotel Accommodation",
        href: "/services/#worldwide-hotel-accommodation",
      },
      {
        title: "Outbound Holiday Packages",
        href: "/services/#outbound-holiday-packages",
      },
      {
        title: "Global Visa Assistance",
        href: "/services/#global-visa-assistance",
      },
      {
        title: "Transfers and Train Tickets",
        href: "/services/#transfer-train-tickets",
      },
      {
        title: "Travel Insurance (Inbound & Outbound)",
        href: "/services/#inbound-outbound",
      },
      {
        title: "UAE Tourist Visa",
        href: "/services/#uae-tourist-visa",
      },
      {
        title: "24/7 Client Assistance",
        href: "/services/#client-assistance",
      },
    ],
  },
];

const toggleCards = [
  {
    title: "Gallery",
    image: gallery,
    href: "/about",
  },
  {
    title: "FAQ",
    image: faq,
    href: "/contact",
  },
  {
    title: "Contact",
    image: contact,
    href: "/contact",
  },
];

const countries = getNames().sort((a, b) => a.localeCompare(b));

// ================= FORM INPUT =================
const FormInput = ({
  label,
  name,
  value,
  onChange,
  error,
  required = false,
  type = "text",
  placeholder = "",
  showPassword,
  onTogglePassword,
}) => {
  const isPassword = type === "password";

  return (
    <div className="w-full">
      <label className="mb-1.5 block text-[13px] font-semibold text-[#263238]">
        {label} {required && <span className="text-red-500">*</span>}
      </label>

      <div className="relative">
        <input
          type={isPassword && showPassword ? "text" : type}
          name={name}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          className={`h-[46px] w-full rounded-lg border bg-white px-4 text-[14px] text-[#263238] outline-none transition-all placeholder:text-gray-400 ${
            error
              ? "border-red-400 focus:border-red-500 focus:ring-2 focus:ring-red-100"
              : "border-[#d1d2d3] focus:border-[#1691D9] focus:ring-2 focus:ring-[#1691D9]/10"
          } ${isPassword ? "pr-12" : ""}`}
        />

        {isPassword && (
          <button
            type="button"
            onClick={onTogglePassword}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[19px] text-gray-500 transition hover:text-[#1691D9]"
          >
            <i
              className={`las ${showPassword ? "la-eye-slash" : "la-eye"}`}
            ></i>
          </button>
        )}
      </div>

      {error && (
        <p className="mt-1 text-[11px] font-medium text-red-500">{error}</p>
      )}
    </div>
  );
};

// ================= AGENT REGISTRATION MODAL =================
const AgentRegistrationModal = ({ onClose }) => {
  const formRef = useRef(null);
  const [form, setForm] = useState({
    affiliateName: "",
    companyAddress: "",
    website: "",
    country: "",
    city: "",
    zipCode: "",

    agentName: "",
    profileAddress: "",
    mobile: "",
    email: "",
    telephone: "",
    preferredCurrency: "",
    whatsapp: "",
    skype: "",
    designation: "",
  });

  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    if (success) {
      setSuccess(false);
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!form.affiliateName.trim()) {
      newErrors.affiliateName = "Affiliate Name is required.";
    }

    if (!form.companyAddress.trim()) {
      newErrors.companyAddress = "Address is required.";
    }

    if (!form.zipCode.trim()) {
      newErrors.zipCode = "Zip Code is required.";
    }

    if (!form.agentName.trim()) {
      newErrors.agentName = "Agent Name is required.";
    }

    if (!form.profileAddress.trim()) {
      newErrors.profileAddress = "Address is required.";
    }

    if (!form.mobile.trim()) {
      newErrors.mobile = "Mobile Number is required.";
    }

    if (!form.email.trim()) {
      newErrors.email = "Email Address is required.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      newErrors.email = "Please enter a valid email address.";
    }

    // Website validation
    if (form.website.trim()) {
      let websiteValue = form.website.trim();

      if (!/^https?:\/\//i.test(websiteValue)) {
        websiteValue = `https://${websiteValue}`;
      }

      try {
        new URL(websiteValue);
      } catch {
        newErrors.website = "Please enter a valid website URL.";
      }
    }

    // Phone validation
    const phoneRegex = /^[0-9+\s().-]{7,20}$/;

    if (form.mobile.trim() && !phoneRegex.test(form.mobile.trim())) {
      newErrors.mobile = "Please enter a valid mobile number.";
    }

    if (form.telephone.trim() && !phoneRegex.test(form.telephone.trim())) {
      newErrors.telephone = "Please enter a valid telephone number.";
    }

    if (form.whatsapp.trim() && !phoneRegex.test(form.whatsapp.trim())) {
      newErrors.whatsapp = "Please enter a valid WhatsApp number.";
    }

    setErrors(newErrors);

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();

    if (Object.keys(validationErrors).length > 0) {
      const firstError = Object.keys(validationErrors)[0];

      setTimeout(() => {
        const element = document.querySelector(`[name="${firstError}"]`);

        if (element) {
          element.focus();
          element.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 50);

      return;
    }

    try {
      setIsSubmitting(true);

      await emailjs.sendForm(
        "service_3x1mr54",
        "template_einceag",
        formRef.current,
        {
          publicKey: "txVJjRtYBet6JGN3H",
        },
      );

      setSuccess(true);

      setForm({
        affiliateName: "",
        companyAddress: "",
        website: "",
        country: "",
        city: "",
        zipCode: "",
        agentName: "",
        profileAddress: "",
        mobile: "",
        email: "",
        telephone: "",
        preferredCurrency: "",
        whatsapp: "",
        skype: "",
        designation: "",
      });
    } catch (error) {
      console.error("EmailJS Error:", error);

      setEmailError("Unable to submit your registration. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-3 backdrop-blur-sm sm:p-5"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          onClose();
        }
      }}
    >
      <div className="relative flex h-[96vh] w-full max-w-5xl overflow-hidden rounded-lg bg-white shadow-[0_30px_100px_rgba(0,0,0,0.35)]">
        {/* ================= LEFT IMAGE ================= */}
        <div className="relative hidden w-[35%] overflow-hidden lg:block">
          <img
            src={contact}
            alt="Voyage Agent"
            className="absolute inset-0 h-full w-full object-cover"
          />

          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(120deg, rgba(18,52,74,0.98) 0%, rgba(0,127,192,0.90) 50%, rgba(1,158,231,0.82) 100%)",
            }}
          ></div>

          <div className="relative z-10 flex h-full flex-col justify-between p-10 text-white">
            <div>
              <div className="inline-flex items-center rounded-full border border-white/25 bg-white/10 px-4 py-2 backdrop-blur-md">
                <span className="mr-2 h-2 w-2 rounded-full bg-[#95BE4C]"></span>
                <span className="text-[12px] font-semibold tracking-[1.5px]">
                  VOYAGE AGENT PORTAL
                </span>
              </div>

              <h2 className="mt-10 max-w-[390px] text-[38px] font-bold leading-[1.1]">
                Grow Your Travel Business With Us
              </h2>

              <p className="mt-5 max-w-[390px] text-[15px] leading-7 text-white/80">
                Join Voyage as a travel partner and access global travel
                solutions, competitive rates and reliable support for your
                customers.
              </p>
            </div>

            <div>
              <img
                src={voyagelogo}
                alt="Voyage"
                className="w-[150px] object-contain brightness-0 invert"
              />

              <p className="mt-4 text-[12px] text-white/60">
                Your trusted partner for worldwide travel solutions.
              </p>
            </div>
          </div>
        </div>

        {/* ================= RIGHT FORM ================= */}
        <div className="relative flex min-w-0 flex-1 flex-col">
          {/* HEADER */}
          <div className="relative shrink-0 border-b border-[#E8EEF1] bg-white px-5 py-5 sm:px-8">
            <button
              type="button"
              onClick={onClose}
              className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-[#F1F5F7] text-[22px] text-gray-500 transition hover:bg-red-50 hover:text-red-500"
              aria-label="Close"
            >
              <i className="las la-times"></i>
            </button>

            <div className="pr-12">
              <p className="text-[11px] font-bold uppercase tracking-[2px] text-[#1691D9]">
                Agent Enquiry
              </p>

              <h2 className="mt-1 md:text-[25px] text-[18px] font-bold text-[#172B36]">
                Become a Travel Partner
              </h2>

              <p className="mt-1 text-[13px] text-gray-500">
                Fill in your details below, and our team will get in touch with
                you.
              </p>
            </div>
          </div>

          {/* FORM SCROLL AREA */}
          <div className="min-h-0 flex-1 overflow-y-auto bg-[#F8FAFB] px-4 py-5 sm:px-7 sm:py-6">
            <form
              ref={formRef}
              id="agent-registration-form"
              onSubmit={handleSubmit}
              className="mx-auto max-w-[850px]"
            >
              {/* ================= COMPANY DETAILS ================= */}
              <div className="mb-5 rounded-2xl border border-[#E5EDF1] bg-white p-5 shadow-[0_4px_18px_rgba(0,0,0,0.03)] sm:p-6">
                <div className="mb-5 flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#1590d9] to-[#1590d9] text-[12px] font-bold text-white">
                    01
                  </div>

                  <div>
                    <h3 className="text-[17px] font-bold text-[#172B36]">
                      Company Details
                    </h3>

                    <p className="text-[11px] text-gray-400">
                      Enter your company information
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <FormInput
                    label="Affiliate Name"
                    name="affiliateName"
                    value={form.affiliateName}
                    onChange={handleChange}
                    error={errors.affiliateName}
                    required
                    placeholder="Enter affiliate name"
                  />

                  <FormInput
                    label="Address"
                    name="companyAddress"
                    value={form.companyAddress}
                    onChange={handleChange}
                    error={errors.companyAddress}
                    required
                    placeholder="Enter company address"
                  />

                  <FormInput
                    label="Website"
                    name="website"
                    value={form.website}
                    onChange={handleChange}
                    error={errors.website}
                    placeholder="www.example.com"
                  />

                  {/* COUNTRY */}
                  <div className="w-full">
                    <label className="mb-1.5 block text-[13px] font-semibold text-[#263238]">
                      Country
                    </label>

                    <select
                      name="country"
                      value={form.country}
                      onChange={handleChange}
                      className="h-[46px] w-full rounded-lg border border-[#d1d2d3] bg-white px-4 text-[14px] text-[#263238] outline-none transition-all focus:border-[#1691D9] focus:ring-2 focus:ring-[#1691D9]/10"
                    >
                      <option value="">Select Country</option>

                      {countries.map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </select>
                  </div>

                  <FormInput
                    label="City"
                    name="city"
                    value={form.city}
                    onChange={handleChange}
                    placeholder="Enter city"
                  />

                  <FormInput
                    label="Zip Code"
                    name="zipCode"
                    value={form.zipCode}
                    onChange={handleChange}
                    error={errors.zipCode}
                    required
                    placeholder="Enter zip code"
                  />
                </div>
              </div>

              {/* ================= PROFILE DETAILS ================= */}
              <div className="mb-5 rounded-2xl border border-[#E5EDF1] bg-white p-5 shadow-[0_4px_18px_rgba(0,0,0,0.03)] sm:p-6">
                <div className="mb-5 flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#1590d9] to-[#1590d9] text-[12px] font-bold text-white">
                    02
                  </div>

                  <div>
                    <h3 className="text-[17px] font-bold text-[#172B36]">
                      Profile Details
                    </h3>

                    <p className="text-[11px] text-gray-400">
                      Provide your personal and contact information
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <FormInput
                    label="Agent Name"
                    name="agentName"
                    value={form.agentName}
                    onChange={handleChange}
                    error={errors.agentName}
                    required
                    placeholder="Enter agent name"
                  />

                  <FormInput
                    label="Address"
                    name="profileAddress"
                    value={form.profileAddress}
                    onChange={handleChange}
                    error={errors.profileAddress}
                    required
                    placeholder="Enter address"
                  />

                  <FormInput
                    label="Mobile"
                    name="mobile"
                    value={form.mobile}
                    onChange={handleChange}
                    error={errors.mobile}
                    required
                    placeholder="Enter mobile number"
                  />

                  <FormInput
                    label="Email Address"
                    name="email"
                    value={form.email}
                    onChange={handleChange}
                    error={errors.email}
                    required
                    type="email"
                    placeholder="Enter email address"
                  />

                  <FormInput
                    label="Telephone Number"
                    name="telephone"
                    value={form.telephone}
                    onChange={handleChange}
                    error={errors.telephone}
                    placeholder="Enter telephone number"
                  />

                  <FormInput
                    label="Preferred Currency"
                    name="preferredCurrency"
                    value={form.preferredCurrency}
                    onChange={handleChange}
                    placeholder="Enter preferred currency"
                  />

                  <FormInput
                    label="WhatsApp Number"
                    name="whatsapp"
                    value={form.whatsapp}
                    onChange={handleChange}
                    error={errors.whatsapp}
                    placeholder="Enter WhatsApp number"
                  />

                  <FormInput
                    label="Skype ID"
                    name="skype"
                    value={form.skype}
                    onChange={handleChange}
                    placeholder="Enter Skype ID"
                  />

                  <FormInput
                    label="Designation"
                    name="designation"
                    value={form.designation}
                    onChange={handleChange}
                    placeholder="Enter designation"
                  />
                </div>
              </div>

              {/* ================= SUCCESS MESSAGE ================= */}
              {success && (
                <div className="mb-5 flex items-center gap-3 rounded-xl border border-green-200 bg-green-50 p-4">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-green-500 text-white">
                    <i className="las la-check"></i>
                  </div>

                  <div>
                    <p className="text-[13px] font-bold text-green-700">
                      Registration Successful
                    </p>

                    <p className="text-[11px] text-green-600">
                      All required details have been completed successfully.
                    </p>
                  </div>
                </div>
              )}

              {/* ================= SUBMIT ================= */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex h-[54px] w-full items-center justify-center gap-2 rounded-lg bg-gradient-to-r from-[#95BE4C] to-[#95BE4C] text-[14px] font-bold text-white shadow-[0_10px_25px_rgba(22,145,217,0.18)] transition-all duration-300 hover:-translate-y-[1px] hover:shadow-[0_14px_30px_rgba(22,145,217,0.25)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmitting ? (
                  <>
                    <i className="las la-spinner la-spin text-[17px]"></i>
                    Sending...
                  </>
                ) : (
                  <>
                    Submit Details
                    <i className="las la-arrow-right text-[17px]"></i>
                  </>
                )}
              </button>

              <p className="mt-4 pb-2 text-center text-[10px] leading-5 text-gray-400">
                By submitting this form, you agree to our terms and conditions
                and confirm that the information provided is accurate.
              </p>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileDropdown, setMobileDropdown] = useState(null);
  const toggleRef = useRef(null);
  const [agentModalOpen, setAgentModalOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };

    window.addEventListener("scroll", handleScroll);

    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? "hidden" : "";

    return () => {
      document.body.style.overflow = "";
    };
  }, [mobileMenuOpen]);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileDropdown(null);
  };

  useEffect(() => {
    const openDestinationModal = () => {
      setAgentModalOpen(true);
    };

    window.addEventListener("open-agent-modal", openDestinationModal);

    return () => {
      window.removeEventListener("open-agent-modal", openDestinationModal);
    };
  }, []);

  return (
    <>
      <header className="fixed left-0 top-0 z-50 w-full">
        <div className="mx-auto max-w-7xl px-4 md:px-0">
          <div className="mt-4 flex gap-2">
            {/* ================= DESKTOP HEADER ================= */}
            <div
              className={`hidden w-full items-center justify-between px-6 transition-all duration-300 md:flex ${
                scrolled
                  ? "rounded-lg bg-white text-black shadow-lg"
                  : "border-b border-white/20 bg-transparent pb-4 text-white"
              }`}
            >
              {/* Logo */}
              <a href="/" className="flex flex-shrink-0 items-center gap-4">
                <img
                  src={scrolled ? voyageheader : voyagelogo}
                  alt="Voyage Logo"
                  className="w-52 object-contain transition-all duration-300"
                />
                <div className="h-10 w-px shrink-0 bg-white/40 mt-4" />

                <img
                  src={scrolled ? IATAlogocolor : IATAlogo}
                  alt="Second Logo"
                  className="w-[80px] shrink-0 object-contain"
                />
              </a>

              {/* Desktop Navigation */}
              <nav className="flex items-center gap-2">
                {menus.map((menu, i) => (
                  <div key={i} className="group relative">
                    <a
                      href={menu.href}
                      className={`flex cursor-pointer items-center gap-2 rounded-lg p-3 text-lg font-semibold transition-all duration-300 ${
                        scrolled
                          ? "text-black hover:text-sky-600"
                          : "text-white hover:text-white"
                      }`}
                    >
                      {menu.title}

                      {menu.items && (
                        <i
                          className={`las la-angle-down text-sm transition-all duration-300 group-hover:rotate-180 ${
                            scrolled
                              ? "text-black group-hover:text-sky-600"
                              : "text-white"
                          }`}
                        ></i>
                      )}
                    </a>

                    {/* Desktop Dropdown */}
                    {menu.items && (
                      <div className="invisible absolute left-1/2 top-full mt-3 w-[350px] -translate-x-1/2 rounded-lg bg-white opacity-0 shadow-lg transition-all duration-300 group-hover:visible group-hover:opacity-100">
                        {menu.dropdownHeader && (
                          <div
                            className={`${menu.dropdownHeader.bg} ${menu.dropdownHeader.text} flex items-start gap-3 rounded-t-lg px-4 py-3`}
                          >
                            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border bg-white">
                              <i
                                className={`${menu.dropdownHeader.icon} text-lg`}
                              ></i>
                            </div>

                            <div>
                              <h4 className="text-sm font-semibold">
                                {menu.dropdownHeader.title}
                              </h4>

                              <p className="mt-1 text-xs opacity-80">
                                {menu.dropdownHeader.description}
                              </p>
                            </div>
                          </div>
                        )}

                        <ul className="py-2">
                          {menu.items.map((item, idx) => (
                            <li key={idx}>
                              <a
                                href={item.href}
                                className="block rounded-md px-4 py-2 text-sm font-medium text-gray-700 transition hover:bg-gray-100 hover:text-sky-700"
                              >
                                {item.title}
                              </a>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
                {/* ================= AGENT SIGN IN ================= */}
                <div className="group relative flex-shrink-0">
                  <button
                    type="button"
                    onClick={() => setAgentModalOpen(true)}
                    aria-label="Agent Sign In"
                    className="relative flex h-[48px] items-center gap-2 rounded-full bg-[#99c04c] px-3 pr-5 text-white shadow-[0_6px_15px_rgba(122,201,67,0.35)] transition-all duration-300 hover:scale-105 hover:bg-[#8DB63F]"
                  >
                    <span className="absolute left-[12px] top-[5px] h-[6px] w-[17px] rotate-[-25deg] rounded-full bg-white/40 blur-[1px]"></span>
                    <span className="relative z-10 flex h-[38px] w-[38px] items-center justify-center rounded-full bg-white/20">
                      <i className="las la-user-tie text-[20px] text-white"></i>
                    </span>
                    <span className="relative z-10 whitespace-nowrap text-[13px] font-medium">
                      Agent Sign Up
                    </span>
                  </button>
                </div>

                {/* Desktop Green Toggle */}
                <div ref={toggleRef} className="group relative">
                  <button
                    type="button"
                    className="group relative flex h-[58px] w-[58px] items-center justify-center rounded-full shadow-[0_8px_20px_rgba(18,52,74,0.10)] transition-all duration-500 hover:scale-110"
                  >
                    <div className="absolute inset-0 scale-75 rounded-full bg-[#97BF49]/15 opacity-0 blur-xl transition-all duration-500 group-hover:scale-125 group-hover:opacity-70"></div>

                    <div className="relative flex h-[46px] w-[46px] items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-[#99c04c] via-[#99c04c] to-[#99c04c] shadow-[0_6px_15px_rgba(122,201,67,0.35)] transition-all duration-500">
                      <span className="absolute left-[7px] top-[5px] h-[6px] w-[17px] rotate-[-25deg] rounded-full bg-[#FEFEFE]/50 blur-[1px]"></span>

                      <span className="absolute right-[-4px] top-[-4px] h-[19px] w-[19px] rounded-full bg-white/20 blur-md"></span>

                      <div className="relative h-6 w-6">
                        <i className="las la-equals absolute inset-0 flex items-center justify-center text-[20px] font-bold text-white transition-all duration-300 group-hover:scale-75 group-hover:rotate-90 group-hover:opacity-0"></i>

                        <i className="las la-angle-up absolute inset-0 flex items-center justify-center text-[20px] font-bold text-white opacity-0 transition-all duration-300 group-hover:scale-100 group-hover:opacity-100"></i>
                      </div>
                    </div>
                  </button>

                  {/* Toggle Cards */}
                  <div className="invisible fixed right-16 top-[100px] w-full opacity-0 transition-all duration-300 group-hover:visible group-hover:opacity-100">
                    <div className="mx-auto max-w-6xl rounded-lg bg-white px-2 py-2 shadow-2xl">
                      <div className="grid grid-cols-3 gap-2">
                        {toggleCards.map((card, i) => (
                          <a
                            key={i}
                            href={card.href}
                            className="group relative h-[300px] overflow-hidden rounded-xl"
                          >
                            <img
                              src={card.image}
                              alt={card.title}
                              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                            />

                            <div className="absolute inset-0 bg-black/20"></div>

                            <div className="absolute bottom-6 left-6 text-2xl font-bold text-white">
                              {card.title}
                            </div>

                            <div className="absolute bottom-6 right-6 flex h-8 w-8 items-center justify-center rounded-full bg-white shadow-lg transition-transform duration-300 group-hover:scale-110">
                              <i className="las la-arrow-right text-base text-black"></i>
                            </div>
                          </a>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Desktop Contact */}
                <div className="group relative flex-shrink-0">
                  <button
                    type="button"
                    className="group relative flex h-[58px] w-[58px] items-center justify-center rounded-full shadow-[0_8px_20px_rgba(18,52,74,0.10)] transition-all duration-300 hover:scale-110"
                  >
                    <div className="absolute inset-0 scale-75 rounded-full bg-[#97BF49]/15 opacity-0 blur-xl transition-all duration-500 group-hover:scale-125 group-hover:opacity-70"></div>

                    <div className="relative flex h-[46px] w-[46px] items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-[#99c04c] via-[#99c04c] to-[#99c04c] shadow-[0_6px_15px_rgba(122,201,67,0.35)]">
                      <span className="absolute left-[7px] top-[5px] h-[6px] w-[17px] rotate-[-25deg] rounded-full bg-white/50 blur-[1px]"></span>

                      <i className="las la-phone-alt relative z-10 text-[20px] text-white drop-shadow-[0_2px_3px_rgba(0,0,0,0.2)]"></i>
                    </div>
                  </button>

                  {/* Contact Dropdown */}
                  <div className="invisible absolute right-0 top-full z-[100] mt-3 w-[390px] max-w-[calc(100vw-32px)] translate-y-2 overflow-hidden rounded-lg border border-gray-100 bg-white opacity-0 shadow-2xl transition-all duration-300 group-hover:visible group-hover:translate-y-0 group-hover:opacity-100">
                    <div className="relative overflow-hidden bg-gradient-to-r from-[#99c04c] via-[#99c04c] to-[#99c04c] px-6 py-3 text-white">
                      <div className="absolute -right-12 -top-16 h-40 w-40 rounded-full bg-white/10"></div>

                      <div className="absolute -bottom-20 -left-16 h-30 w-40 rounded-full bg-white/10"></div>

                      <div className="relative flex items-start gap-4">
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-white/20 bg-white/20 backdrop-blur-sm">
                          <i className="las la-phone-alt text-2xl"></i>
                        </div>

                        <div className="flex-1">
                          <h3 className="text-xl font-bold leading-tight">
                            Get in Touch
                          </h3>

                          <p className="mt-2 text-sm leading-5 text-white/90">
                            Connect with our travel experts to plan your perfect
                            journey
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gray-50 p-5">
                      <a
                        href="tel:043888222"
                        className="mb-4 flex w-full items-center gap-4 rounded-lg bg-white p-2 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
                      >
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#99c04c] to-[#99c04c] text-white">
                          <i className="las la-phone-alt text-2xl"></i>
                        </div>

                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-800">
                            Call Now
                          </p>

                          <p className="mt-1 text-base font-bold text-[#00A651]">
                            04 388 8222
                          </p>
                        </div>
                      </a>

                      <a
                        href="https://wa.me/971565443990"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mb-4 flex w-full items-center gap-4 rounded-lg bg-white p-2 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
                      >
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#99c04c] to-[#99c04c] text-white ">
                          <i className="lab la-whatsapp text-2xl"></i>
                        </div>

                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-800">
                            Whatsapp
                          </p>

                          <p className="mt-1 text-base font-bold text-[#00A651]">
                            +971 56 544 3990
                          </p>
                        </div>
                      </a>

                      <a
                        href="mailto:info@voyageplustravel.com"
                        className="mb-5 flex w-full items-center gap-4 rounded-lg bg-white p-2 shadow-sm transition-all duration-300 hover:-translate-y-0.5 hover:shadow-md"
                      >
                        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-[#2198d8] to-[#2198d8] text-white ">
                          <i className="las la-envelope text-2xl"></i>
                        </div>

                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold text-gray-800">
                            Email Us
                          </p>

                          <p className="mt-1 truncate text-base font-bold text-blue-600">
                            info@voyageplustravel.com
                          </p>
                        </div>
                      </a>

                      {/* <div className="mb-4 flex w-full items-center justify-center gap-2 rounded-xl border border-green-100 bg-green-50 px-3 py-3">
                        <span className="h-2.5 w-2.5 flex-shrink-0 animate-pulse rounded-full bg-green-500"></span>

                        <span className="text-sm font-semibold text-green-700">
                          Available 9 AM - 6 PM (Mon-Fri)
                        </span>
                      </div> */}

                      <a
                        href="/contact"
                        className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#99c04c] to-[#99c04c] text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:from-[#5AAE2D] hover:to-[#438D22] hover:shadow-xl"
                      >
                        <i className="las la-file-invoice text-lg"></i>

                        <span>Connect With Us</span>
                      </a>
                    </div>
                  </div>
                </div>
              </nav>
            </div>

            {/* ================= MOBILE HEADER ================= */}
            <div
              className={`flex w-full items-center justify-between py-2 md:hidden ${
                scrolled
                  ? "rounded-lg bg-white shadow-lg px-4"
                  : "bg-transparent px-0"
              }`}
            >
              {/* Mobile Logo */}
              <a href="/" className="flex items-center gap-2">
                <img
                  src={scrolled ? voyageheader : voyagelogo}
                  alt="Voyage Logo"
                  className="w-40 object-contain"
                />
                <div className="h-10 w-px shrink-0 bg-white/40 mt-4" />

                <img
                  src={scrolled ? IATAlogocolor : IATAlogo}
                  alt="Second Logo"
                  className="w-[60px] shrink-0 object-contain mt-2"
                />
              </a>

              {/* Mobile Menu Button */}
              <button
                type="button"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Open menu"
                className="relative flex h-[52px] w-[52px] items-center justify-center rounded-full shadow-[0_8px_20px_rgba(18,52,74,0.10)]"
              >
                <div className="absolute inset-0 rounded-full bg-[#97BF49]/15 blur-xl"></div>

                <div className="relative flex h-[44px] w-[44px] items-center justify-center overflow-hidden rounded-full bg-gradient-to-br from-[#97BF49] via-[#7AC943] to-[#4F9D24] shadow-[0_6px_15px_rgba(122,201,67,0.35)]">
                  <span className="absolute left-[7px] top-[5px] h-[6px] w-[17px] rotate-[-25deg] rounded-full bg-white/50 blur-[1px]"></span>

                  <span className="absolute right-[-4px] top-[-4px] h-[19px] w-[19px] rounded-full bg-white/20 blur-md"></span>

                  <i className="las la-bars relative z-10 text-[21px] text-white"></i>
                </div>
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* ================= MOBILE OVERLAY ================= */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/50 md:hidden"
          onClick={closeMobileMenu}
        ></div>
      )}

      {/* ================= MOBILE RIGHT SLIDER ================= */}
      <div
        className={`fixed right-0 top-0 z-[110] h-screen w-full max-w-[390px] bg-white shadow-2xl transition-transform duration-500 ease-in-out md:hidden ${
          mobileMenuOpen ? "translate-x-0" : "translate-x-full"
        }`}
      >
        {/* Mobile Slider Header */}
        <div className="flex h-[80px] items-center justify-between border-b border-gray-100 bg-gradient-to-r from-[#12344A] to-[#007FC0] px-5">
          <a href="/" onClick={closeMobileMenu}>
            <img
              src={voyageheader}
              alt="Voyage Logo"
              className="w-40 object-contain"
            />
          </a>

          <button
            type="button"
            onClick={closeMobileMenu}
            aria-label="Close menu"
            className="flex h-10 w-10 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25"
          >
            <i className="las la-times text-2xl"></i>
          </button>
        </div>

        {/* Mobile Menu Content */}
        <div className="h-[calc(100vh-80px)] overflow-y-auto px-5 py-6">
          <div className="mb-6">
            <p className="text-xs font-semibold uppercase tracking-[3px] text-[#97BF49]">
              Explore
            </p>

            <h2 className="mt-2 text-2xl font-bold text-[#12344A]">
              Discover Your Journey
            </h2>
          </div>

          {/* Mobile Main Menus */}
          <div className="space-y-2">
            {menus.map((menu, index) => (
              <div
                key={index}
                className="overflow-hidden rounded-xl border border-gray-100 bg-gray-50"
              >
                <div className="flex items-center justify-between">
                  <a
                    href={menu.href}
                    onClick={closeMobileMenu}
                    className="flex-1 px-4 py-4 text-base font-semibold text-[#12344A]"
                  >
                    {menu.title}
                  </a>

                  {menu.items && (
                    <button
                      type="button"
                      onClick={() =>
                        setMobileDropdown(
                          mobileDropdown === index ? null : index,
                        )
                      }
                      className="flex h-12 w-12 items-center justify-center text-[#12344A]"
                    >
                      <i
                        className={`las la-angle-down transition-transform duration-300 ${
                          mobileDropdown === index ? "rotate-180" : ""
                        }`}
                      ></i>
                    </button>
                  )}
                </div>

                {/* Mobile Dropdown */}
                {menu.items && mobileDropdown === index && (
                  <div className="border-t border-gray-100 bg-white px-3 py-2">
                    {menu.items.map((item, itemIndex) => (
                      <a
                        key={itemIndex}
                        href={item.href}
                        onClick={closeMobileMenu}
                        className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium text-gray-700 transition hover:bg-[#019EE7]/10 hover:text-[#007FC0]"
                      >
                        <span className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-[#019EE7]/10">
                          <i className="las la-arrow-right text-xs text-[#019EE7]"></i>
                        </span>

                        <span>{item.title}</span>
                      </a>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Mobile Quick Links */}
          <div className="mt-6">
            <button
              type="button"
              onClick={() => {
                closeMobileMenu();
                setAgentModalOpen(true);
              }}
              className="group relative flex h-[54px] w-full items-center justify-center gap-3 overflow-hidden rounded-xl bg-[#99c04c] px-5 text-white shadow-[0_8px_20px_rgba(122,201,67,0.25)] transition-all duration-300 hover:bg-[#8DB63F] hover:shadow-lg"
            >
              {/* Shine */}
              <span className="absolute left-8 top-2 h-[7px] w-20 -rotate-[20deg] rounded-full bg-white/30 blur-[2px] transition-all duration-500 group-hover:left-14"></span>

              {/* Icon */}
              <span className="relative z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/20">
                <i className="las la-user-tie text-[20px] text-white"></i>
              </span>

              {/* Text */}
              <span className="relative z-10 text-[14px] font-bold tracking-wide">
                Agent Sign Up
              </span>

              {/* Arrow */}
              <i className="las la-arrow-right relative z-10 text-[17px] transition-transform duration-300 group-hover:translate-x-1"></i>
            </button>
          </div>
          <div className="mt-6">
            <p className="mb-3 text-xs font-bold uppercase tracking-[2px] text-gray-400">
              Quick Links
            </p>

            <div className="grid grid-cols-3 gap-2">
              {toggleCards.map((card, index) => (
                <a
                  key={index}
                  href={card.href}
                  onClick={closeMobileMenu}
                  className="group relative h-[100px] overflow-hidden rounded-xl"
                >
                  <img
                    src={card.image}
                    alt={card.title}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />

                  <div className="absolute inset-0 bg-black/35"></div>

                  <div className="absolute bottom-2 left-2 text-xs font-bold text-white">
                    {card.title}
                  </div>
                </a>
              ))}
            </div>
          </div>

          {/* Mobile Contact */}
          <div className="mt-6 rounded-2xl bg-gradient-to-br from-[#12344A] to-[#007FC0] p-5 text-white">
            <p className="text-xs font-semibold uppercase tracking-[2px] text-white/60">
              Need Help?
            </p>

            <h3 className="mt-2 text-xl font-bold">
              Talk to our travel experts
            </h3>

            <div className="mt-5 space-y-3">
              <a
                href="tel:043888222"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 rounded-xl bg-white/10 p-3 transition hover:bg-white/20"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#97BF49]">
                  <i className="las la-phone-alt"></i>
                </span>

                <span>
                  <span className="block text-xs text-white/60">Call Now</span>
                  <span className="font-semibold">04 388 8222</span>
                </span>
              </a>

              <a
                href="https://wa.me/971565443990"
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 rounded-xl bg-white/10 p-3 transition hover:bg-white/20"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#97BF49]">
                  <i className="lab la-whatsapp text-lg"></i>
                </span>

                <span>
                  <span className="block text-xs text-white/60">WhatsApp</span>
                  <span className="font-semibold">+971 56 544 3990</span>
                </span>
              </a>

              <a
                href="mailto:info@voyageplustravel.com"
                onClick={closeMobileMenu}
                className="flex items-center gap-3 rounded-xl bg-white/10 p-3 transition hover:bg-white/20"
              >
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#019EE7]">
                  <i className="las la-envelope"></i>
                </span>

                <span className="min-w-0">
                  <span className="block text-xs text-white/60">Email</span>
                  <span className="block truncate font-semibold">
                    info@voyageplustravel.com
                  </span>
                </span>
              </a>
            </div>

            <a
              href="/contact"
              onClick={closeMobileMenu}
              className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-[#97BF49] to-[#7AC943] text-sm font-bold text-white shadow-lg transition hover:shadow-xl"
            >
              <i className="las la-file-invoice"></i>
              <span>Connect With Us</span>
            </a>
          </div>
        </div>
      </div>

      {agentModalOpen && (
        <AgentRegistrationModal onClose={() => setAgentModalOpen(false)} />
      )}
    </>
  );
};

export default Header;
